import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog"
import { cleanSpeakerName, isGenericSpeakerLabel } from './protocol/speakerLabels';

const EditSpeakersModal = ({ isOpen, onClose, speakers, onSave,
                             voiceIdEnabled = false, voiceIdReady = true }) => {
  const [editedSpeakers, setEditedSpeakers] = useState(
    speakers.reduce((acc, участник) => ({ ...acc, [участник]: участник }), {})
  );
  // Кого запомнить по голосу. Пусто по умолчанию: голосовой отпечаток
  // сохраняется только по явной галочке человека, никогда молча.
  const [rememberVoices, setRememberVoices] = useState({});

  // Пересинхронизируем поля с актуальным списком участников: компонент часто
  // смонтирован заранее с speakers=[] (имена приходят позже), а useState-
  // инициализатор отрабатывает только раз — без этого поля оставались бы
  // пустыми. Зависимость по содержимому списка: пока модалка открыта и список
  // не меняется, ввод пользователя не сбрасывается.
  useEffect(() => {
    setEditedSpeakers(speakers.reduce((acc, участник) => ({ ...acc, [участник]: участник }), {}));
    setRememberVoices({});  // новая запись — галочки не переносим
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speakers.join(' ')]);

  const handleУчастникChange = (originalName, newName) => {
    setEditedSpeakers(prev => ({ ...prev, [originalName]: newName }));
  };

  const handleSave = () => {
    // Запоминаем всех отмеченных, у кого в поле стоит человеческое имя, — в том
    // числе когда имя не меняли. Это единственный способ добавить второй эталон
    // уже узнанному человеку: его метка и так подписана именем, а на сравнении
    // со средним по нескольким записям всё узнавание и держится.
    // Заглушки вида «Участник 2» в базу голосов не пускаем.
    const remembered = Object.keys(rememberVoices).filter((участник) => {
      const name = cleanSpeakerName(editedSpeakers[участник]);
      return rememberVoices[участник] && name && !isGenericSpeakerLabel(name);
    });
    onSave(editedSpeakers, remembered);
    onClose();
  };

  // Сортировка участников по номеру (от 1 к большему)
  const sortedУчастники = [...speakers].sort((a, b) => {
    const aMatch = a.match(/\d+/);
    const bMatch = b.match(/\d+/);

    if (aMatch && bMatch) {
      return parseInt(aMatch[0]) - parseInt(bMatch[0]);
    }

    if (aMatch) return -1;
    if (bMatch) return 1;

    return a.localeCompare(b);
  });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:rounded-2xl p-0 gap-0 border-0 shadow-xl">
        <DialogHeader className="px-5 pt-5 pb-3">
          <DialogTitle className="text-base font-semibold text-paper-ink">
            Редактировать имена участников
          </DialogTitle>
        </DialogHeader>
        <div className="px-5 pb-4 flex flex-col gap-3">
          {sortedУчастники.map(участник => (
            <div key={участник} className="flex flex-col gap-1">
              <label
                htmlFor={`участник-${участник}`}
                className="text-xs font-medium text-paper-ink-3"
              >
                {участник}
              </label>
              <input
                id={`участник-${участник}`}
                type="text"
                autoComplete="off"
                value={editedSpeakers[участник]}
                onChange={(e) => handleУчастникChange(участник, e.target.value)}
                className="px-3 py-2 rounded-panel bg-paper-card text-sm font-montserrat text-paper-ink outline-none transition-all focus: focus:ring-[3px] focus:ring-violet-100"
              />
              {voiceIdEnabled && (
                <label
                  htmlFor={`запомнить-${участник}`}
                  className={`flex items-center gap-2 text-xs ${voiceIdReady
                    ? 'text-paper-ink-3 cursor-pointer' : 'text-paper-hint cursor-not-allowed'}`}
                >
                  <input
                    id={`запомнить-${участник}`}
                    type="checkbox"
                    disabled={!voiceIdReady}
                    checked={!!rememberVoices[участник]}
                    onChange={(e) => setRememberVoices((prev) => ({
                      ...prev, [участник]: e.target.checked,
                    }))}
                    className="accent-violet-600"
                  />
                  {voiceIdReady
                    ? 'Запомнить голос — в следующих записях подпишем имя сами'
                    : 'Запомнить голос можно только в свежей расшифровке — эта открыта из сохранённой'}
                </label>
              )}
            </div>
          ))}
        </div>
        <DialogFooter className="px-5 py-3 border-t border-paper-line sm:justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-panel bg-paper-card text-sm font-medium text-paper-ink-2 hover:bg-paper-panel transition-colors"
          >
            Отмена
          </button>
          <button
            id="button-speakers-save"
            type="button"
            onClick={handleSave}
            className="px-4 py-2 rounded-panel border-0 bg-paper-accent text-sm font-semibold text-white shadow-sm shadow-violet-200 hover:bg-paper-accent-hover transition-colors"
          >
            Сохранить
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditSpeakersModal;